import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AlertTitle,
    Avatar,
    Badge,
    Box,
    Chip,
    CircularProgress,
    Button,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemIcon,
    ListItemSecondaryAction,
    ListItemText,
    Paper,
    Stack,
    Tooltip,
    TextField,
    Typography,
    useTheme
} from "@mui/material";
import dayjs from "dayjs";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {useCallback, useMemo, useState} from "react";
import {useApplicationContext} from "../ApplicationContext";
import {DownloadFileButton} from "./DownloadFileButton";
import {MarkdownRenderer} from "./MarkdownRenderer";
import {DeleteFileDialg} from "./Admin/DeleteFileDialog";
import {
    EUserGroup,
    VersionTreeNodeDto,
    VersionVersionFileData
} from "../apiClient/Api";

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {
    faPlus,
    faStars,
    faFileArrowDown,
    faUser,
    faTriangleExclamation,
    faDownload,
    faWandMagicSparkles,
    faCalendar,
    faTrash,
    faCaretDown,
    faChevronDown,
    faChevronUp,
    faFolderOpen,
    faPaperPlane,
    faBan,
    faRotateLeft
} from '@fortawesome/pro-solid-svg-icons'
import {useTranslation} from "react-i18next";
import {getStageLabel, loadVersionFiles} from "../utils/versionUtils";

export interface VersionItemProps {
    data: VersionTreeNodeDto
    reloadCallback?: () => void
}

type HostSystemFileGroup = {
    id: string
    name: string
    files: VersionVersionFileData[]
}

const groupFilesByHostSystem = (
    files: VersionVersionFileData[] | undefined,
    unknownLabel: string
): HostSystemFileGroup[] => {
    const groups = new Map<string, HostSystemFileGroup>();
    const order: string[] = [];
    (files ?? []).forEach((file) => {
        const groupId = file.hostSystemId ?? 'unknown';
        const preferredName = file.hostSystemName?.trim() ?? '';
        const groupName = preferredName || unknownLabel;
        if (!groups.has(groupId)) {
            groups.set(groupId, {id: groupId, name: groupName, files: []});
            order.push(groupId);
        }
        const group = groups.get(groupId);
        if (group) {
            if (preferredName && group.name === unknownLabel) {
                group.name = preferredName;
            }
            group.files.push(file);
        }
    });
    return order.map((id) => groups.get(id)).filter(Boolean) as HostSystemFileGroup[];
};

const ChangeList = ({items, icon, color}: { items: string[]; icon: any; color: string }) => (
    <List dense disablePadding>
        {items.filter(e => e.length > 0).map((elem, idx) => (
            <ListItem key={idx} sx={{paddingTop: '2px', paddingBottom: '2px', alignItems: 'flex-start'}}>
                <ListItemIcon sx={{minWidth: 28, color, mt: '4px'}}>
                    <FontAwesomeIcon icon={icon} fontSize={14}/>
                </ListItemIcon>
                <ListItemText
                    primary={elem}
                    slotProps={{primary: {variant: 'body2', sx: {lineHeight: 1.45}}}}
                />
            </ListItem>
        ))}
    </List>
);

export const VersionItem = (props: VersionItemProps) => {
    const {t} = useTranslation();
    const changes = props.data.changes;
    const context = useApplicationContext();
    const theme = useTheme();
    const [downloadsOpen, setDownloadsOpen] = useState(false);
    const [sendMailToAllOpen, setSendMailToAllOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<VersionVersionFileData | undefined>(undefined);
    const [selectedFileDelete, setSelectedFileDelete] = useState<string | undefined>(undefined);
    const [mailContent, setMailContent] = useState<string>("");

    const [files, setFiles] = useState<VersionVersionFileData[] | undefined>(undefined);
    const [filesShown, setFilesShown] = useState(false);
    const [filesLoading, setFilesLoading] = useState(false);

    const hostSystemUnknownLabel = t('version.item.hostSystemUnknown');
    const groupedFiles = useMemo(
        () => groupFilesByHostSystem(files, hostSystemUnknownLabel),
        [files, hostSystemUnknownLabel]
    );

    const fileDeleted = useCallback((deleted: boolean) => {
        setSelectedFileDelete(undefined);
        if (deleted && props.reloadCallback) {
            props.reloadCallback();
        }
    }, [props]);

    const isCritical = (props.data.criticalUpdate?.length ?? 0) > 0;
    const isRevoked = !!props.data.revokedAt;
    const isDownloadBlocked = props.data.downloadBlocked === true;
    const stageLabel = getStageLabel(props.data.stage, t);

    const handleToggleFiles = useCallback(async () => {
        if (filesShown) {
            setFilesShown(false);
            return;
        }
        if (files === undefined) {
            const productId = props.data.productId;
            const major = props.data.major;
            const minor = props.data.minor;
            const patch = props.data.patch;
            if (productId == null || major == null || minor == null || patch == null) {
                return;
            }
            setFilesLoading(true);
            try {
                const loaded = await loadVersionFiles(context.api, productId, major, minor, patch);
                setFiles(loaded);
            } catch (error) {
                context.apiError?.(error);
            } finally {
                setFilesLoading(false);
            }
        }
        setFilesShown(true);
    }, [filesShown, files, context, props.data.major, props.data.minor, props.data.patch, props.data.productId]);

    const handleCloseDownloads = () => setDownloadsOpen(false);
    const handleCloseMail = () => {
        setMailContent("");
        setSendMailToAllOpen(false);
    };

    const sendMailToAll = () => {
        if (!mailContent || mailContent.length < 10) {
            alert("Sie müssen mindestens 10 Zeichen eingeben um eine Mail zu verschicken");
            return;
        }
        if (window.confirm("Möchten Sie wirklich eine Mail an alle schicken?")) {
            context.api.version
                .versionSendMailToUsers(props.data.major!, props.data.minor!, props.data.patch!, mailContent)
                .then(() => handleCloseMail())
                .catch(context.apiError);
        }
    };

    const hasContent = (str: string | string[] | null | undefined) => {
        if (Array.isArray(str)) return str.filter(e => e.length > 0).length > 0;
        return !!str && (typeof str === 'string' ? str.length > 0 : true);
    };

    const isPrimaryVersion = () => {
        const patch = props.data.patch ?? -1;
        const minor = props.data.minor ?? -1;
        return minor === -1 || patch === -1;
    };

    const versionLabel = `${t('version.item.version', {version: isPrimaryVersion() && "ersion "})}${props.data.major}${(props.data.minor ?? -1) !== -1 ? `.${props.data.minor}${(props.data.patch ?? -1) !== -1 ? `.${props.data.patch}` : ""}` : ""}${stageLabel ? ` ${stageLabel}` : ""}`;

    const featuresList = (changes?.features ?? []).filter(e => e.length > 0);
    const bugfixesList = (changes?.bugfixes ?? []).filter(e => e.length > 0);
    const highlightsList = (changes?.highlights ?? []).filter(e => e.length > 0);

    const internalInfo = props.data.internalInformation;

    return (
        <>
            <Paper
                elevation={0}
                id={props.data.versionId!}
                sx={{
                    scrollMarginTop: 80,
                    position: 'relative',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    borderLeft: isCritical
                        ? `4px solid ${theme.palette.error.main}`
                        : isRevoked
                            ? `4px solid ${theme.palette.warning.main}`
                            : `1px solid ${theme.palette.divider}`,
                    overflow: 'hidden',
                    opacity: isRevoked ? 0.95 : 1,
                    transition: 'box-shadow 0.2s',
                    '&:hover': {
                        boxShadow: theme.shadows[2]
                    }
                }}
            >
                <Box sx={{p: {xs: 2, sm: 3}}}>
                    {/* Compact header row */}
                    <Stack
                        direction={{xs: 'column', sm: 'row'}}
                        spacing={1}
                        sx={{mb: 1.5, alignItems: {xs: 'flex-start', sm: 'center'}, justifyContent: 'space-between'}}
                    >
                        <Stack direction="row" spacing={1.5} sx={{alignItems: 'center', flexWrap: 'wrap'}}>
                            <Typography
                                component="h2"
                                variant="h5"
                                color="primary"
                                sx={{fontWeight: 700, lineHeight: 1.2, m: 0}}
                            >
                                {versionLabel}
                            </Typography>
                            {stageLabel && (
                                <Chip
                                    label={stageLabel}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    sx={{fontWeight: 600}}
                                />
                            )}
                            {isCritical && (
                                <Chip
                                    icon={<FontAwesomeIcon icon={faTriangleExclamation}/>}
                                    label={t('version.item.criticalUpdate', {update: ''}).replace(/:?\s*$/, '')}
                                    size="small"
                                    color="error"
                                    sx={{fontWeight: 600}}
                                />
                            )}
                            {isRevoked && (
                                <Chip
                                    icon={<FontAwesomeIcon icon={faRotateLeft}/>}
                                    label={t('version.item.revoked')}
                                    size="small"
                                    color="warning"
                                    sx={{fontWeight: 600}}
                                />
                            )}
                            {isDownloadBlocked && (
                                <Chip
                                    icon={<FontAwesomeIcon icon={faBan}/>}
                                    label={t('version.item.downloadBlocked')}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{fontWeight: 600}}
                                />
                            )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>
                            <FontAwesomeIcon icon={faCalendar} fontSize="inherit"/>{' '}
                            {dayjs(props.data.approved).format("DD.MM.YYYY")}
                        </Typography>
                    </Stack>

                    {props.data.subtitle && (
                        <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1}}>
                            {props.data.subtitle}
                        </Typography>
                    )}

                    {isCritical && (
                        <Alert severity="error" icon={<FontAwesomeIcon icon={faTriangleExclamation}/>} sx={{mb: 2}}>
                            {props.data.criticalUpdate}
                        </Alert>
                    )}

                    {isRevoked && (
                        <Alert severity="warning" icon={<FontAwesomeIcon icon={faRotateLeft}/>} sx={{mb: 2}}>
                            <AlertTitle sx={{fontWeight: 700}}>{t('version.item.revoked')}</AlertTitle>
                            {props.data.revokedReason || t('version.item.revokedDescription')}
                        </Alert>
                    )}

                    {hasContent(props.data.header) && props.data.minor !== -1 && props.data.patch !== -1 && (
                        <Box sx={{mb: 2}}>
                            <MarkdownRenderer>{props.data.header!}</MarkdownRenderer>
                        </Box>
                    )}

                    {/* Highlights — full width, top */}
                    {highlightsList.length > 0 && (
                        <Box
                            sx={{
                                mb: 2,
                                p: 1.5,
                                borderRadius: 1.5,
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255,255,255,0.04)'
                                    : 'rgba(0,0,0,0.025)'
                            }}
                        >
                            <Typography
                                variant="overline"
                                color="secondary"
                                sx={{fontWeight: 700, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}
                            >
                                <FontAwesomeIcon icon={faStars}/>
                                {t('version.item.highlights')}
                            </Typography>
                            <ChangeList items={highlightsList} icon={faStars} color={theme.palette.secondary.main}/>
                        </Box>
                    )}

                    {/* Features + Bugfixes side-by-side */}
                    {(featuresList.length > 0 || bugfixesList.length > 0) && (
                        <Grid container spacing={2} sx={{mb: 2}}>
                            {featuresList.length > 0 && (
                                <Grid size={{xs: 12, md: bugfixesList.length > 0 ? 6 : 12}}>
                                    <Typography
                                        variant="overline"
                                        color="primary"
                                        sx={{fontWeight: 700, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1}}
                                    >
                                        <FontAwesomeIcon icon={faPlus}/>
                                        {t('version.item.features')}
                                    </Typography>
                                    <ChangeList items={featuresList} icon={faPlus} color={theme.palette.primary.main}/>
                                </Grid>
                            )}
                            {bugfixesList.length > 0 && (
                                <Grid size={{xs: 12, md: featuresList.length > 0 ? 6 : 12}}>
                                    <Typography
                                        variant="overline"
                                        color="success.main"
                                        sx={{fontWeight: 700, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1}}
                                    >
                                        <FontAwesomeIcon icon={faWandMagicSparkles}/>
                                        {t('version.item.bugfixes')}
                                    </Typography>
                                    <ChangeList items={bugfixesList} icon={faWandMagicSparkles} color={theme.palette.success.main}/>
                                </Grid>
                            )}
                        </Grid>
                    )}

                    {hasContent(props.data.description) && (
                        <>
                            <Divider sx={{my: 2}}/>
                            <Typography variant="subtitle2" color="text.secondary" sx={{fontWeight: 700, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5}}>
                                {t('version.item.information')}
                            </Typography>
                            <MarkdownRenderer>{props.data.description!}</MarkdownRenderer>
                        </>
                    )}

                    {context.isInternal && hasContent(internalInfo) && (
                        <>
                            <Divider sx={{my: 2}}/>
                            <Typography variant="subtitle2" color="warning.main" sx={{fontWeight: 700, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5}}>
                                {t('version.item.internalInformation')}
                            </Typography>
                            <MarkdownRenderer>{internalInfo!}</MarkdownRenderer>
                        </>
                    )}

                    {/* Files — hidden by default, lazy-loaded */}
                    <Divider sx={{my: 2}}/>
                    <Button
                        onClick={handleToggleFiles}
                        disabled={filesLoading}
                        variant={filesShown ? "outlined" : "contained"}
                        color="primary"
                        size="small"
                        startIcon={
                            filesLoading
                                ? <CircularProgress size={14} color="inherit"/>
                                : <FontAwesomeIcon icon={faFolderOpen}/>
                        }
                        endIcon={<FontAwesomeIcon icon={filesShown ? faChevronUp : faChevronDown}/>}
                        sx={{textTransform: 'none', fontWeight: 600}}
                    >
                        {filesLoading
                            ? t('version.item.loadingFiles')
                            : filesShown
                                ? t('version.item.hideFiles')
                                : t('version.item.showFiles')}
                        {files !== undefined && files.length > 0 && !filesLoading && (
                            <Chip
                                size="small"
                                label={files.length}
                                sx={{ml: 1, height: 18, fontSize: 11}}
                                color={filesShown ? "default" : "secondary"}
                            />
                        )}
                    </Button>

                    {filesShown && !filesLoading && (
                        <Box sx={{mt: 2}}>
                            {isDownloadBlocked && (
                                <Alert severity="warning" icon={<FontAwesomeIcon icon={faBan}/>} sx={{mb: 2}}>
                                    <AlertTitle sx={{fontWeight: 700}}>{t('version.item.downloadBlocked')}</AlertTitle>
                                    {props.data.downloadBlockedReason || t('version.item.downloadBlockedDescription')}
                                </Alert>
                            )}
                            {files === undefined || files.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic'}}>
                                    {t('version.item.noFiles')}
                                </Typography>
                            ) : (
                                <Stack spacing={1}>
                                    {groupedFiles.map((group) => (
                                        <FileGroup
                                            key={group.id}
                                            group={group}
                                            t={t}
                                            downloadBlocked={isDownloadBlocked}
                                            downloadBlockedReason={props.data.downloadBlockedReason ?? undefined}
                                            onShowDownloads={(file) => {
                                                setSelectedFile(file);
                                                setDownloadsOpen(true);
                                            }}
                                            onDeleteFile={(id) => setSelectedFileDelete(id)}
                                            canSeeDownloads={(context.userData?.userGroup ?? 4) <= EUserGroup.OFFICE}
                                            canDelete={(context.userData?.userGroup ?? 4) <= EUserGroup.ADMIN}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    )}

                    {hasContent(props.data.footer) && (
                        <>
                            <Divider sx={{my: 2}}/>
                            <MarkdownRenderer>{props.data.footer!}</MarkdownRenderer>
                        </>
                    )}

                    {(context.userData?.userGroup ?? EUserGroup.CUSTOMER) <= EUserGroup.ADMIN && (
                        <Button
                            sx={{mt: 2, textTransform: 'none'}}
                            fullWidth
                            variant="text"
                            color="inherit"
                            size="small"
                            startIcon={<FontAwesomeIcon icon={faPaperPlane}/>}
                            onClick={() => setSendMailToAllOpen(true)}
                        >
                            {t('version.item.sendEmailToAll')}
                        </Button>
                    )}
                </Box>
            </Paper>

            <DeleteFileDialg fileDeleted={fileDeleted} id={selectedFileDelete}/>

            <Dialog
                open={sendMailToAllOpen}
                fullWidth
                maxWidth="md"
                onClose={handleCloseMail}
            >
                <DialogTitle>{t('version.item.sendMail')}</DialogTitle>
                <DialogContent>
                    <Typography variant="caption" sx={{fontWeight: 'bold'}}>
                        {t('version.item.sendMailToAllDescription')}
                    </Typography>
                    <TextField
                        multiline
                        rows={5}
                        fullWidth
                        value={mailContent}
                        onChange={(e) => setMailContent(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button color="warning" onClick={sendMailToAll}>
                        <FontAwesomeIcon icon={faTriangleExclamation}/>{' '}
                        {t('version.item.sendEmailToAll')}
                    </Button>
                    <Button onClick={handleCloseMail}>{t('version.item.close')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={downloadsOpen}
                fullWidth
                maxWidth="md"
                onClose={handleCloseDownloads}
            >
                <DialogTitle>{t('version.item.downloads')}</DialogTitle>
                <DialogContent>
                    <List>
                        {selectedFile?.downloadedBy?.map((data, idx) => (
                            <ListItem key={idx}>
                                <ListItemText primary={data.name} secondary={data.eMail}/>
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDownloads}>{t('version.item.close')}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

type FileGroupProps = {
    group: HostSystemFileGroup
    t: ReturnType<typeof useTranslation>['t']
    onShowDownloads: (file: VersionVersionFileData) => void
    onDeleteFile: (id: string) => void
    canSeeDownloads: boolean
    canDelete: boolean
    downloadBlocked: boolean
    downloadBlockedReason?: string
}

const FileGroup = ({group, t, onShowDownloads, onDeleteFile, canSeeDownloads, canDelete, downloadBlocked, downloadBlockedReason}: FileGroupProps) => {
    const theme = useTheme();
    const blockTooltip = downloadBlockedReason || t('version.item.downloadBlockedDescription');
    return (
        <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1.5,
                '&:before': {display: 'none'},
                '&.Mui-expanded': {margin: 0}
            }}
        >
            <AccordionSummary
                expandIcon={<FontAwesomeIcon icon={faCaretDown}/>}
                sx={{
                    minHeight: 40,
                    '& .MuiAccordionSummary-content': {margin: '8px 0', alignItems: 'center'}
                }}
            >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flex: 1}}>
                    <FontAwesomeIcon icon={faFileArrowDown} color={theme.palette.primary.main}/>
                    <Typography variant="body2" sx={{fontWeight: 600}}>
                        {group.name}
                    </Typography>
                    <Chip
                        size="small"
                        label={group.files.length}
                        sx={{height: 20, fontSize: 11}}
                    />
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{paddingLeft: 0, paddingRight: 0, paddingTop: 0}}>
                <List dense disablePadding>
                    {group.files.map((file) => (
                        <ListItem
                            key={file.id ?? file.fileName}
                            sx={{
                                paddingRight: {xs: '50px', sm: '95px'}
                            }}
                        >
                            {downloadBlocked ? (
                                <Tooltip title={blockTooltip} arrow>
                                    <Box sx={{display: 'flex', alignItems: 'center', flex: 1, gap: 1, opacity: 0.55, cursor: 'not-allowed'}}>
                                        <ListItemAvatar sx={{display: {xs: 'none', sm: 'block'}, minWidth: 44}}>
                                            <Avatar sx={{width: 32, height: 32, backgroundColor: theme.palette.action.disabledBackground}}>
                                                <FontAwesomeIcon icon={faBan} fontSize={14}/>
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={file.type?.name ?? ''}
                                            secondary={file.fileName}
                                            slotProps={{
                                                primary: {variant: 'body2', sx: {fontWeight: 600}},
                                                secondary: {variant: 'caption'}
                                            }}
                                        />
                                    </Box>
                                </Tooltip>
                            ) : (
                                <>
                                    <DownloadFileButton id={file.id!} fileName={file.fileName!}>
                                        <ListItemAvatar sx={{display: {xs: 'none', sm: 'block'}, minWidth: 44}}>
                                            <Avatar sx={{width: 32, height: 32}}>
                                                <FontAwesomeIcon icon={faDownload} fontSize={14}/>
                                            </Avatar>
                                        </ListItemAvatar>
                                    </DownloadFileButton>
                                    <DownloadFileButton id={file.id!} fileName={file.fileName!}>
                                        <ListItemText
                                            primary={file.type?.name ?? ''}
                                            secondary={file.fileName}
                                            slotProps={{
                                                primary: {variant: 'body2', sx: {fontWeight: 600}},
                                                secondary: {variant: 'caption'}
                                            }}
                                        />
                                    </DownloadFileButton>
                                </>
                            )}

                            <ListItemSecondaryAction sx={{width: {xs: '40px', sm: '90px'}}}>
                                {canSeeDownloads && (file.downloadedBy?.length ?? 0) > 0 && (
                                    <IconButton
                                        onClick={() => onShowDownloads(file)}
                                        size="small"
                                    >
                                        <Badge
                                            badgeContent={file.downloadedBy?.length}
                                            color="primary"
                                        >
                                            <FontAwesomeIcon icon={faUser}/>
                                        </Badge>
                                    </IconButton>
                                )}
                                {canDelete && (
                                    <IconButton
                                        color="warning"
                                        sx={{display: {xs: 'none', sm: 'inline-flex'}}}
                                        size="small"
                                        onClick={() => file.id && onDeleteFile(file.id)}
                                    >
                                        <FontAwesomeIcon icon={faTrash}/>
                                    </IconButton>
                                )}
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            </AccordionDetails>
        </Accordion>
    );
};
